import React, { createContext, useContext, useState, useEffect } from "react";
import { moreTranslations } from "./PageTranslations";

export type Language = "en" | "es" | "fr" | "de" | "hi" | "zh" | "ja" | "ar";

export interface LanguageConfig {
  code: Language;
  name: string;
  localName: string;
  flag: string;
  dir: "ltr" | "rtl";
}

export const LANGUAGES: LanguageConfig[] = [
  { code: "en", name: "English", localName: "English", flag: "🇺🇸", dir: "ltr" },
  { code: "es", name: "Spanish", localName: "Español", flag: "🇪🇸", dir: "ltr" },
  { code: "fr", name: "French", localName: "Français", flag: "🇫🇷", dir: "ltr" },
  { code: "de", name: "German", localName: "Deutsch", flag: "🇩🇪", dir: "ltr" },
  { code: "hi", name: "Hindi", localName: "हिन्दी", flag: "🇮🇳", dir: "ltr" },
  { code: "zh", name: "Chinese", localName: "简体中文", flag: "🇨🇳", dir: "ltr" },
  { code: "ja", name: "Japanese", localName: "日本語", flag: "🇯🇵", dir: "ltr" },
  { code: "ar", name: "Arabic", localName: "العربية", flag: "🇸🇦", dir: "rtl" },
];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, section?: string, fallback?: string) => any;
  currentConfig: LanguageConfig;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const translations: Record<Language, Record<string, any>> = {
  en: {
    common: {
      getInTouch: "Get In Touch",
      visit: "Visit",
      reset: "Reset",
      seconds: "s",
      back: "Back",
      required: "Required",
      verify: "Verify",
      send: "Send Message",
    },
    navbar: {
      aiAutomations: "AI Automations",
      products: "Products",
      philosophy: "Philosophy",
      about: "About",
      contact: "Contact",
      cta: "Get In Touch",
      navigation: "Navigation",
      emailUs: "careers@teralumax.com",
      ctaMobile: "Start A Project",
    },
    hero: {
      tag: "Crafted Digital Architecture",
      line1: "Building Products",
      line2: "That People Love.",
      sub: "Teralumax creates beautifully crafted software products and digital experiences designed for modern businesses and everyday users.",
      ctaPrimary: "Explore Products",
      ctaSecondary: "Get In Touch",
      scrollDown: "Scroll down",
    },
    about: {
      philosophyTag: "The Philosophy",
      header: "Craftsmanship Over Compromise",
      quotePart1: "We build software with ",
      quoteSimplicity: "simplicity",
      quotePart2: ", ",
      quotePrecision: "precision",
      quotePart3: ", and absolute ",
      quotePurpose: "purpose",
      quotePart4: ".",
      specializations: "Specializations /",
      bento1Title: "Impeccable Specs",
      bento1Desc: "Our codebases are engineered with stringent type bounds, modular systems, and hardware optimizations to maximize responsiveness.",
      bento2Title: "Design-Led Velocity",
      bento2Desc: "We design parallelly to development, engineering polished vector curves, micro-feedback, and sensory micro-animations directly.",
      bento3Title: "Human Utility",
      bento3Desc: "Every digital interface should act as a natural companion to business logic, simplifying workflow constraints instead of compounding them.",
      words: [
        "SaaS Platforms",
        "Tailored Automation",
        "Immersive Web Apps",
        "Native Mobile Apps",
        "Cognitive AI Tools",
        "Robust Cloud Core",
        "Bold Digital Innovation",
      ],
    },
    roomy: {
      tag: "PRODUCT SHOWCASE / 01",
      title: "Roomy™",
      sub: "AI-Powered Housing, PG, & Compatible Roommate Discovery Platform.",
      desc: "Roomy™ eliminates broker fees and roommate friction. Find verified apartments, PGs, mess facilities, and compatible flatmates based on verified lifestyle habits and budget preferences.",
      feature1: "Verified Listings",
      feature2: "AI Compatibility Matching",
      feature3: "Zero Brokerage Fees",
      feature4: "PG & Mess Discovery",
      feature5: "Integrated Expense Tracker",
      liveWebsite: "LIVE WEBSITE",
      visitRoomy: "Visit Live Site",
      nav1: "Find Properties",
      nav2: "Match Roommates",
      nav3: "PG & Mess",
      nav4: "List Property",
      getStarted: "Get Started",
      bannerTag: "ZERO BROKERAGE • VERIFIED LISTINGS",
      bannerTitle: "Find Your Perfect Roommate & Next Home Without Broker Fees",
      bannerDesc: "Search over 12,000+ verified apartments, PGs, and lifestyle-matched flatmates in major tech hubs.",
      searchBtn: "Search Homes",
      card1Tag: "Apartment",
      zeroBrokerage: "Zero Brokerage",
      card1Title: "Skyline Premium 2BHK",
      fullyFurnished: "Fully Furnished",
      card2Tag: "Co-Living PG",
      mealsIncluded: "Meals Included",
      threeMeals: "3 Meals Daily",
      card3Subtitle: "Software Engineer • Veg",
      card3Bio: "\"Looking for a peaceful room near Tech Park. Non-smoker, loves acoustic guitar.\"",
      compatibilityMatch: "96% Compatibility Match",
      verified: "Verified",
      footerTitle: "Ready to explore Roomy™?",
      footerDesc: "Discover verified properties, roomies, and zero-brokerage listings today.",
      footerBtn: "Visit Official Roomy™ Webpage",
      statOccupancy: "Occupancy State",
      statOccupancyDesc: "Live utilization rate",
      statBookings: "Completed Swipes",
      statBookingsDesc: "Instant API synchronizations",
    },
    siteforces: {
      tag: "PRODUCT SHOWCASE / 02",
      title: "SiteForces",
      sub: "Construction Workforce & Attendance Management Built for Modern Contractors.",
      desc: "SiteForces helps construction companies manage workers across multiple sites with QR-based check-ins, automated daily wage calculations, attendance tracking, and Excel reporting.",
      feature1: "QR Site Check-ins",
      feature2: "Daily Wage Tracking",
      feature3: "Multi-Site Management",
      feature4: "Automated Payroll Reports",
      feature5: "Excel Data Sync",
      livePlatform: "LIVE PLATFORM",
      visitSiteForces: "Visit Live Platform",
      nav1: "Active Sites",
      nav2: "Worker Roster",
      nav3: "QR Attendance",
      nav4: "Wage Payroll",
      dashboardLogin: "Dashboard Login",
      activeSitesLabel: "ACTIVE SITES",
      workersTrackedLabel: "WORKERS TRACKED",
      qrCheckinsLabel: "QR CHECK-INS TODAY",
      dailyWageLabel: "DAILY WAGE PAYROLL",
      rosterTitle: "LIVE SITE ATTENDANCE ROSTER",
      liveSync: "LIVE SYNC",
      colWorker: "Worker Name",
      colSite: "Construction Site",
      colTrade: "Trade / Role",
      colCheckin: "Check-In",
      colWage: "Daily Wage",
      qrCardTitle: "QR Code Site Verification",
      qrCardDesc: "Site managers print durable QR posters at entry gates. Workers scan with any smartphone camera to check in instantly without app downloads.",
      excelReady: "Excel Export Ready",
      footerTitle: "Interested in SiteForces?",
      footerDesc: "Streamline construction site attendance, QR check-ins, and daily wages.",
      footerBtn: "Visit Official SiteForces Webpage",
      tags: ["Low-Code Node Sync", "JSON Schema Output", "Visual Stylesheets", "Subsecond Reload"],
      hudEngine: "SITEFORCES_CORE_ENGINE",
      hudState: "ENGINE_ACTIVE",
      hudStatus: "STATUS: OPTIMAL_SYNC",
      hudNode: "Relational Website Node",
      hudView: "Visual Dynamic Siteforces View",
      hudDesc: "Automated visual tree generation linked directly to relational tables",
      hudLoad: "PAGE LOAD",
      hudLoadSpeed: "0.02s EXTREME",
    },
    arrowescape: {
      tag: "PRODUCT SHOWCASE / 03",
      title: "Arrow Escape",
      subtitle: "Google Play Store Original",
      sub: "Minimalist Precision Physics & Infinite Maze Puzzle Game.",
      desc: "Arrow Escape is a fast-paced physics puzzle game featuring procedural maze generation, responsive touch/keyboard glide physics, global leaderboards, and sleek neon visual aesthetics.",
      feature1: "Infinite Procedural Mazes",
      feature2: "Precision Physics Mechanics",
      feature3: "Global Speedrun Leaderboards",
      feature4: "Smooth 120FPS Controls",
      feature5: "Google Play & Web SDK",
      googlePlayTag: "GOOGLE PLAY",
      playStoreCTA: "Google Play Store",
      nav1: "Game Overview",
      nav2: "Global Leaderboard",
      nav3: "Controls & Physics",
      nav4: "Play Store",
      launchWebGame: "Launch Web Game",
      bannerTag: "NEON PRECISION PHYSICS PUZZLE",
      bannerTitle: "Glide Through Procedural Mazes in Seconds",
      bannerDesc: "Master directional vector momentum, dodge barrier traps, and compete for top speedrun ranks globally.",
      playBtn: "Get on Google Play",
      availableGooglePlay: "Available on Google Play Store",
      mazeVariationsLabel: "MAZE VARIATIONS",
      frameRateLabel: "FRAME RATE",
      communityRatingLabel: "COMMUNITY RATING",
      footerTitle: "Experience Arrow Escape",
      footerDesc: "Download directly from Google Play Store today.",
      footerBtn: "View on Google Play Store",
      statMoves: "Moves Computed",
      statMovesUnit: "Steps",
      statMovesUnitSingle: "Step",
      statStatus: "Labyrinth Status",
      statusEscaped: "ESCAPED!",
      statusActive: "Active Labyrinth Gold Live",
      retry: "Reset",
      emulatorInstruction: "Press Arrow Keys or WASD to navigate. Reach the green portal (Finish) at the bottom-right corner.",
    },
    careers: {
      tag: "Open Pathways",
      title: "Join Our Journey",
      desc: "We are constantly looking for self-directed engineering, design, and product generalists who find pride in pixel accuracy and high-performance algorithms.",
      applyCta: "Apply via Email",
      applySub: "Send CV to careers@teralumax.com",
      perks: [
        { title: "Remote Craftsmanship", text: "Asynchronous autonomy with fluid spatial workflows." },
        { title: "Vanguard Tech Bounds", text: "Write strict, optimized TS interfaces with standard-setting rendering speeds." },
        { title: "Absolute Detail Focus", text: "No templates. We build bespoke solutions with pristine care." },
      ],
    },
    contact: {
      tag: "Reach Out",
      title: "Get In Touch",
      desc: "Have a project, a collaboration opportunity, or simply want to inquire about our software engineering pipelines? Write below.",
      labelName: "Your Full Name",
      labelEmail: "Email Address",
      labelCompany: "Company Name",
      labelMessage: "Project Outline / Message",
      placeholderName: "e.g. Liam Sterling",
      placeholderEmail: "e.g. liam@company.com",
      placeholderCompany: "e.g. Sterling Holdings (Optional)",
      placeholderMessage: "Tell us about your objectives...",
      errName: "Please fill out your name to start.",
      errEmail: "Please provide a valid email address so we can reach you.",
      errMessage: "Please enter a short message describing your objectives.",
      successTitle: "Handshake Successful",
      successText: "Your secure packet has been routed. Our core engineering node will initiate a custom response within 12 standard solar cycles.",
      btnLoading: "Transmitting Encrypted Payload...",
      btnSubmit: "Transmit Handshake",
    },
    footer: {
      philosophy: "We design, code, and deploy lightweight elegant products and cloud interfaces for digital native startups across the globe.",
      registeredOffice: "Reg. Office: Delhi, IN",
      allRightsReserved: "Teralumax Private Limited. All rights reserved.",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "Teralumax Secure Gateway",
      controlPanel: "Operational Control Panel",
      nodeRef: "NODE_REF: TL-990-SECURE",
      notFoundTitle: "404 Not Found",
      notFoundText: "The requested URL was not found on this server. That’s all we know.",
      overrideTitle: "[DEMO ONLY] Authorized Override Gate",
      overrideText: "To test the IP restriction mechanics, authorization was suspended for your active IP. Click authorize to append it to the valid list.",
      authorizeBtn: "AUTHORIZE CURRENT CLIENT IP",
      verificationRequired: "Secure Verification Required",
      verificationDesc: "This workstation node is hidden under strict cryptographic handshakes. Provide access seeds to sync records.",
      passphraseLabel: "Alpha Seed Key Passphrase",
      passphrasePlaceholder: "Enter Administrative Seed",
      passphraseBtn: "Initiate handshake",
      totpLabel: "Multi-Factor OTP Pin",
      totpSubLabel: "Active Synchronised 2FA",
      totpPlaceholder: "Enter 6-digit Code",
      totpDeviceSeed: "Authenticator Seed Device",
      totpHint: "Enter the active 6-digit verification code from your authenticator application",
      workspaceTitle: "Control Centre / V1",
      tabLeads: "Leads Vault",
      tabSecurity: "IP Authorization",
      tabTelemetry: "Live Telemetry",
      lockBtn: "Lock Dashboard",
      leadsTitle: "Leads & Contacts Database",
      leadsDesc: "Client requests, messages, and inquiries mapped through encrypted forms.",
      archived: "Archived",
      emptyLeadsTitle: "All Clear! No Pending Leads",
      emptyLeadsDesc: "All secure client handshakes completed successfully. Fill out the corporate form in the contact section to seed more entries!",
      reply: "Reply",
      securityTitle: "IP Firewall Restrictions",
      securityDesc: "Configure allowed client IP coordinates dynamically. Access attempts matching unlisted networks trigger standard 404 masks.",
      allowedAddresses: "Allowed Addresses CIDR",
      activeClientBadge: "Your Active Client",
      ipFormLabel: "Authorize New Node IP",
      ipFormPlaceholder: "e.g. 192.168.1.1",
      ipFormWarn: "Be extremely careful not to strip your active address from allow lists or you will instantly lock your gate!",
      ipFormBtn: "Add to Allowlist",
      emergencyReset: "Reset Defaults (Emergency Restore)",
      telemetryTitle: "System Performance Monitor",
      telemetryDesc: "Live hardware resource telemetry compiled from cloud clusters.",
      cpuLoad: "CPU INGRESS LOAD",
      cpuSub: "Subsecond clock jitters",
      ramLoad: "MEMORY ALLOCATION",
      ramSub: "GB of 32.0 GB allocated",
      dbCore: "DATABASE CORE",
      dbStatus: "ACTIVE",
      dbInstance: "PostgreSQL instance",
      dbSchema: "Siteforces schema active",
      dbDelay: "Replication delay: 8ms",
    }
  },
  es: {
    common: {
      getInTouch: "Contactar",
      visit: "Visitar",
      reset: "Restablecer",
      seconds: "s",
      back: "Atrás",
      required: "Requerido",
      verify: "Verificar",
      send: "Enviar Mensaje",
    },
    navbar: {
      products: "Productos",
      philosophy: "Filosofía",
      about: "Nosotros",
      contact: "Contacto",
      cta: "Contactar",
      navigation: "Navegación",
      emailUs: "careers@teralumax.com",
      ctaMobile: "Iniciar Proyecto",
    },
    hero: {
      tag: "Arquitectura Digital Artesanal",
      line1: "Creando Productos",
      line2: "Que la Gente Ama.",
      sub: "Teralumax crea productos de software y experiencias digitales hermosamente artesanales, diseñadas para empresas modernas y usuarios cotidianos.",
      ctaPrimary: "Explorar Productos",
      ctaSecondary: "Contactar",
      scrollDown: "Deslizar abajo",
    },
    about: {
      philosophyTag: "La Filosofía",
      header: "Artesanía sobre Compromiso",
      quotePart1: "Creamos software con ",
      quoteSimplicity: "simplicidad",
      quotePart2: ", ",
      quotePrecision: "precisión",
      quotePart3: " y un propósito ",
      quotePurpose: "absoluto",
      quotePart4: ".",
      specializations: "Especialidades /",
      bento1Title: "Especificaciones Impecables",
      bento1Desc: "Nuestras bases de código están diseñadas con límites de tipo rigurosos, sistemas modulares y optimizaciones de hardware para maximizar la velocidad de respuesta.",
      bento2Title: "Velocidad Guiada por Diseño",
      bento2Desc: "Diseñamos en paralelo al desarrollo, creando directamente curvas vectoriales pulidas, micro-comentarios y microanimaciones sensoriales.",
      bento3Title: "Utilidad Humana",
      bento3Desc: "Cada interfaz digital debe actuar como un compañero natural de la lógica empresarial, simplificando las limitaciones del flujo de trabajo en lugar de complicarlas.",
      words: [
        "Plataformas SaaS",
        "Automatización a la Medida",
        "Aplicaciones Web Inmersivas",
        "Apps Móviles Nativas",
        "Herramientas de IA Cognitiva",
        "Núcleo de Nube Robusto",
        "Innovación Digital Audaz",
      ],
    },
    roomy: {
      tag: "Presentación de Producto / 01",
      title: "Roomy",
      sub: "Elevando la arquitectura espacial local con software de gestión de salas limpio y nativo de la nube.",
      desc: "Roomy reinventa la programación de espacios de trabajo. Los usuarios reclaman escritorios en tiempo real, sincronizan agendas en tableros de equipo y obtienen información visual sobre los límites de las instalaciones físicas de inmediato.",
      statOccupancy: "Estado de Ocupación",
      statOccupancyDesc: "Tasa de utilización en vivo",
      statBookings: "Registros Completados",
      statBookingsDesc: "Sincronizaciones instantáneas de API",
      visitRoomy: "Visitar Roomy",
    },
    siteforces: {
      tag: "Presentación de Producto / 02",
      title: "SiteForces",
      sub: "Diseño de sitios web visuales modulares vinculados inmediatamente a bases de datos relacionales de alto rendimiento.",
      desc: "SiteForces cambia por completo el paradigma del diseño. Cree bloques magníficos, sincronice tablas de estilo y experimente procesos de implementación ultrarrápidos diseñados para alojar bases de datos completas sin complejidad de código.",
      tags: ["Sincronización de Nodo", "Salida de Esquema JSON", "Hojas de Estilo Visuales", "Recarga en Subsegundos"],
      hudEngine: "NÚCLEO_SITEFORCES",
      hudState: "MOTOR_ACTIVO",
      hudStatus: "ESTADO: SINCRONIZACIÓN_ÓPTIMA",
      hudNode: "Nodo de Sitio Web Relacional",
      hudView: "Vista Dinámica Siteforces",
      hudDesc: "Generación automática del árbol visual vinculado directamente a tablas relacionales",
      hudLoad: "CARGA DE PÁGINA",
      hudLoadSpeed: "0.02s EXTREMO",
      visitSiteForces: "Visitar SiteForces",
    },
    arrowescape: {
      tag: "Presentación de Producto / 03",
      title: "Arrow Escape: Infinite Maze",
      subtitle: "Original de Google Play Store",
      sub: "Esquiva, deslízate y escapa a través de hermosos laberintos de neón de velocidad compuesta.",
      desc: "Arrow Escape combina mecánicas retro con retroalimentación visual moderna. Deslízate por los obstáculos, calcula movimientos instantáneos bajo límites de tiempo y domina la precisión geométrica.",
      statMoves: "Movimientos Calculados",
      statMovesUnit: "Pasos",
      statMovesUnitSingle: "Paso",
      statStatus: "Estado del Laberinto",
      statusEscaped: "¡ESCAPADO!",
      statusActive: "Laberinto Activo en Vivo",
      retry: "Restablecer",
      emulatorInstruction: "Usa las flechas o WASD para navegar. Llega al portal verde (Llegada) en la esquina inferior derecha.",
      playStoreCTA: "Explorar en Google Play",
    },
    careers: {
      tag: "Caminos Abiertos",
      title: "Únete a Nuestro Viaje",
      desc: "Buscamos constantemente generalistas de ingeniería, diseño y producto con iniciativa propia que se enorgullezcan de la precisión de píxeles y algoritmos de alto rendimiento.",
      applyCta: "Postular por Email",
      applySub: "Envía tu CV a careers@teralumax.com",
      perks: [
        { title: "Artesanía Remota", text: "Autonomía asíncrona con flujos de trabajo espaciales corporativos fluidos." },
        { title: "Límites Tecnológicos Vanguardistas", text: "Escribe interfaces TS estrictas y optimizadas con velocidades increíbles." },
        { title: "Enfoque Absoluto al Detalle", text: "Sin plantillas genéricas. Construimos soluciones a medida con un cuidado impecable." },
      ],
    },
    contact: {
      tag: "Contacto",
      title: "Ponte en Contacto",
      desc: "¿Tienes un proyecto, una oportunidad de colaboración o simplemente quieres consultar sobre nuestros flujos de desarrollo? Escríbenos.",
      labelName: "Tu Nombre Completo",
      labelEmail: "Correo Electrónico",
      labelCompany: "Nombre de la Empresa",
      labelMessage: "Descripción del Proyecto / Mensaje",
      placeholderName: "ej. Liam Sterling",
      placeholderEmail: "ej. liam@empresa.com",
      placeholderCompany: "ej. Sterling Holdings (Opcional)",
      placeholderMessage: "Cuéntanos sobre tus objetivos de software...",
      errName: "Por favor, escribe tu nombre para comenzar.",
      errEmail: "Proporciona una dirección de correo válida para contactarte.",
      errMessage: "Por favor, ingresa un mensaje corto describiendo tus objetivos.",
      successTitle: "Conexión Exitosa",
      successText: "Tu paquete seguro ha sido enviado. Nuestro nodo de ingeniería iniciará una respuesta dentro de los próximos 12 ciclos solares.",
      btnLoading: "Transmitiendo Datos Encriptados...",
      btnSubmit: "Transmitir Información",
    },
    footer: {
      philosophy: "Diseñamos, programamos e implementamos productos elegantes y ligeros para startups digitales de todo el mundo.",
      registeredOffice: "Oficina Reg: Delhi, IN",
      allRightsReserved: "Teralumax Private Limited. Todos los derechos reservados.",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "Puerta de Enlace Teralumax",
      controlPanel: "Panel de Control Operativo",
      nodeRef: "REF_NODO: TL-990-SECURE",
      notFoundTitle: "404 No Encontrado",
      notFoundText: "La URL solicitada no se encontró en este servidor. Es todo lo que sabemos.",
      overrideTitle: "[SOLO DEMO] Puerta de Anulación Autorizada",
      overrideText: "Para probar la restricción de IP, se suspendió la seguridad de tu dirección activa. Haz clic para autorizarla en la lista válida.",
      authorizeBtn: "AUTORIZAR IP DEL CLIENTE ACTUAL",
      verificationRequired: "Verificación de Seguridad Requerida",
      verificationDesc: "Este nodo de trabajo está protegido por estrictos protocolos criptográficos. Proporciona el token de verificación para sincronizar.",
      passphraseLabel: "Frase de Seguridad Semilla Alfa",
      passphrasePlaceholder: "Ingrese la semilla administrativa",
      passphraseBtn: "Iniciar apretón de manos",
      totpLabel: "Pin OTP de Multi-Factor",
      totpSubLabel: "2FA Sincronizado Activo",
      totpPlaceholder: "Código de 6 dígitos",
      totpDeviceSeed: "Dispositivo de Autenticación Sincronizado",
      totpHint: "Ingrese el código de verificación activo de 6 dígitos de su aplicación autenticadora",
      workspaceTitle: "Centro de Control / V1",
      tabLeads: "Bóveda de Leads",
      tabSecurity: "Autorización de IP",
      tabTelemetry: "Telemetría en Vivo",
      lockBtn: "Bloquear Panel",
      leadsTitle: "Base de Datos de Leads",
      leadsDesc: "Mensajes, solicitudes de clientes y consultas mapeadas a través de formularios encriptados.",
      archived: "Archivado",
      emptyLeadsTitle: "¡Todo Despejado! Sin solicitudes pendientes",
      emptyLeadsDesc: "Todas las conexiones de clientes se completaron con éxito. ¡Rellena el formulario en la web para agregar más entradas!",
      reply: "Responder",
      securityTitle: "Restricciones de Cortafuegos por IP",
      securityDesc: "Configura dinámicamente las coordenadas de IP del cliente permitidas. Los intentos de acceso no autorizados mostrarán un error 404.",
      allowedAddresses: "Direcciones CIDR Permitidas",
      activeClientBadge: "Tu Cliente Activo",
      ipFormLabel: "Autorizar Nueva IP de Nodo",
      ipFormPlaceholder: "ej. 192.168.1.1",
      ipFormWarn: "¡Ten mucho cuidado de no eliminar tu dirección IP actual de la lista permitida para evitar bloqueos instantáneos!",
      ipFormBtn: "Agregar a la Lista",
      emergencyReset: "Restablecer Valores Predeterminados (Emergencia)",
      telemetryTitle: "Monitor de Rendimiento del Sistema",
      telemetryDesc: "Datos de hardware en vivo compilados de servidores en la nube.",
      cpuLoad: "CARGA DE CPU",
      cpuSub: "Micro oscilaciones de reloj",
      ramLoad: "ALOCACIÓN DE MEMORIA",
      ramSub: "GB de 32.0 GB asignados",
      dbCore: "NÚCLEO DE BASE DE DATOS",
      dbStatus: "ACTIVO",
      dbInstance: "Instancia PostgreSQL",
      dbSchema: "Esquema Siteforces activo",
      dbDelay: "Retraso de réplica: 8ms",
    }
  },
  fr: {
    common: {
      getInTouch: "Nous Contacter",
      visit: "Visiter",
      reset: "Réinitialiser",
      seconds: "s",
      back: "Retour",
      required: "Requis",
      verify: "Vérifier",
      send: "Envoyer le message",
    },
    navbar: {
      products: "Produits",
      philosophy: "Philosophie",
      about: "À Propos",
      contact: "Contact",
      cta: "Nous Contacter",
      navigation: "Navigation",
      emailUs: "careers@teralumax.com",
      ctaMobile: "Démarrer un projet",
    },
    hero: {
      tag: "Architecture Numérique Artisanale",
      line1: "Créer des Produits",
      line2: "Que les Gens Adorent.",
      sub: "Teralumax crée des produits logiciels et des expériences numériques magnifiquement conçus pour les entreprises modernes et les utilisateurs quotidiens.",
      ctaPrimary: "Explorer les Produits",
      ctaSecondary: "Nous Contacter",
      scrollDown: "Faire défiler",
    },
    about: {
      philosophyTag: "La Philosophie",
      header: "L'Artisanat sans Compromis",
      quotePart1: "Nous construisons des logiciels avec ",
      quoteSimplicity: "simplicité",
      quotePart2: ", ",
      quotePrecision: "précision",
      quotePart3: " et un but ",
      quotePurpose: "absolu",
      quotePart4: ".",
      specializations: "Spécialisations /",
      bento1Title: "Specs Impeccables",
      bento1Desc: "Nos bases de code sont conçues avec des typages stricts, des systèmes modulaires et des optimisations matérielles pour maximiser la réactivité.",
      bento2Title: "Vélocité Guidée par le Design",
      bento2Desc: "Nous concevons en parallèle du développement, en intégrant directement des courbes vectorielles épurées, des micro-retours et des micro-animations sensorielles.",
      bento3Title: "Utilité Humaine",
      bento3Desc: "Chaque interface numérique doit agir comme un compagnon naturel de la logique métier, simplifiant les contraintes de flux de travail au lieu de les formuler de manière complexe.",
      words: [
        "Plateformes SaaS",
        "Automatisation sur Mesure",
        "Applications Web Immersives",
        "Applications Mobiles Natives",
        "Outils d'IA Cognitive",
        "Cœur Cloud Robuste",
        "Innovation Numérique Audacieuse",
      ],
    },
    roomy: {
      tag: "Présentation Produit / 01",
      title: "Roomy",
      sub: "Élever l'architecture spatiale locale avec un logiciel de gestion de salles propre et cloud-natif.",
      desc: "Roomy réinvente la planification des espaces de travail. Les utilisateurs réservent des bureaux en temps réel, synchronisent les horaires sur les tableaux d'équipe et obtiennent instantanément des informations visuelles sur les limites physiques des installations.",
      statOccupancy: "État d'Occupation",
      statOccupancyDesc: "Taux d'utilisation en direct",
      statBookings: "Passages Terminés",
      statBookingsDesc: "Sincronisations d'API instantanées",
      visitRoomy: "Visiter Roomy",
    },
    siteforces: {
      tag: "Présentation Produit / 02",
      title: "SiteForces",
      sub: "Conception de sites Web visuels modulaires reliés directement à des bases de données relationnelles haute performance.",
      desc: "SiteForces inverse le paradigme de la mise en page. Créez des blocs magnifiques, synchronisez les tables de style et découvrez des pipelines de déploiement ultra-rapides conçus pour héberger des bases de données entières sans complexité de code.",
      tags: ["Sync de nœud Low-Code", "Schéma de sortie JSON", "Feuilles de style visuelles", "Rechargement ultra-rapide"],
      hudEngine: "SITEFORCES_MOTEUR_CENTRAL",
      hudState: "MOTEUR_ACTIF",
      hudStatus: "STATUT: SYNC_OPTIMAL",
      hudNode: "Nœud de Site Web Relationnel",
      hudView: "Vue Dynamique Siteforces",
      hudDesc: "Génération automatique d'arborescence visuelle liée directement aux tables relationnelles",
      hudLoad: "TEMPS DE CHARGE",
      hudLoadSpeed: "0.02s EXTRÊME",
      visitSiteForces: "Visiter SiteForces",
    },
    arrowescape: {
      tag: "Présentation Produit / 03",
      title: "Arrow Escape: Infinite Maze",
      subtitle: "Original de Google Play Store",
      sub: "Tissez, glissez et échappez-vous à travers de magnifiques labyrinthes de néon à vélocité croissante.",
      desc: "Arrow Escape associe des mécaniques rétro à un retour visuel moderne. Évitez les obstacles, calculez des mouvements instantanés dans des délais serrés et maîtrisez la précision géométrique.",
      statMoves: "Mouvements Calculés",
      statMovesUnit: "Étapes",
      statMovesUnitSingle: "Étape",
      statStatus: "Statut du Labyrinthe",
      statusEscaped: "ÉCHAPPÉ !",
      statusActive: "Labyrinthe Actif en Direct",
      retry: "Réinitialiser",
      emulatorInstruction: "Utilisez les touches fléchées ou WASD pour naviguer. Atteignez le portail vert (Arrivée) en bas à droite.",
      playStoreCTA: "Découvrir sur Google Play",
    },
    careers: {
      tag: "Voies Ouvertes",
      title: "Rejoignez l'Aventure",
      desc: "Nous recherchons constamment des ingénieurs, designers et concepteurs de produits autonomes qui tirent fierté de la précision au pixel près et des algorithmes hautement performants.",
      applyCta: "Postuler par E-mail",
      applySub: "Envoyez votre CV à careers@teralumax.com",
      perks: [
        { title: "Artisanat à Distance", text: "Autonomie asynchrone avec des flux de travail spatiaux fluides." },
        { title: "Limites Technologiques", text: "Écrivez des interfaces TS strictes et optimisées avec des vitesses exceptionnelles." },
        { title: "Focalisation sur le Détail", text: "Pas de modèles préfaits. Nous concevons des solutions sur mesure avec un soin méticuleux." },
      ],
    },
    contact: {
      tag: "Contact",
      title: "Écrivez-nous",
      desc: "Vous avez un projet, une opportunité de collaboration ou vous souhaitez simplement poser une question sur notre ingénierie logicielle ? Écrivez ci-dessous.",
      labelName: "Votre Nom Complet",
      labelEmail: "Adresse E-mail",
      labelCompany: "Nom de l'Entreprise",
      labelMessage: "Aperçu du Projet / Message",
      placeholderName: "ex. Liam Sterling",
      placeholderEmail: "ex. liam@entreprise.com",
      placeholderCompany: "ex. Sterling Holdings (Optionnel)",
      placeholderMessage: "Parlez-nous de vos objectifs technologiques...",
      errName: "Veuillez remplir votre nom pour commencer.",
      errEmail: "Veuillez fournir une adresse e-mail valide afin d'être recontacté.",
      errMessage: "Veuillez entrer un court message décrivant vos objectifs.",
      successTitle: "Transmission réussie",
      successText: "Votre paquet sécurisé a été acheminé. Notre pôle d'ingénierie lancera une réponse personnalisée sous 12 cycles solaires standards.",
      btnLoading: "Transmission cryptée en cours...",
      btnSubmit: "Transmettre les Informations",
    },
    footer: {
      philosophy: "Nous concevons, développons et déployons des produits élégants et des interfaces cloud légères pour les startups du monde entier.",
      registeredOffice: "Siège Social: Delhi, IN",
      allRightsReserved: "Teralumax Private Limited. Tous droits réservés.",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "Passerelle de Sécurité Teralumax",
      controlPanel: "Panneau de Contrôle Opérationnel",
      nodeRef: "REF_NŒUD: TL-990-SECURE",
      notFoundTitle: "404 Non Trouvé",
      notFoundText: "L'URL demandée n'a pas été trouvée sur ce serveur. C'est tout ce que nous savons.",
      overrideTitle: "[DEMO UNIQUEMENT] Porte de Contournement",
      overrideText: "Pour tester les mécanismes d'IP, l'accès sécurisé a été ouvert pour votre IP active. Cliquez sur autoriser pour l'ajouter à la liste valide.",
      authorizeBtn: "AUTORISER L'IP DU CLIENT ACTUEL",
      verificationRequired: "Vérification de Sécurité Requise",
      verificationDesc: "Cette station de travail est masquée par des protocoles cryptographiques stricts. Fournissez les clés d'accès pour synchroniser.",
      passphraseLabel: "Phrase d'accès d'amorçage Alpha",
      passphrasePlaceholder: "Entrer la clé administrative",
      passphraseBtn: "Initier la négociation",
      totpLabel: "Code de vérification multi-facteurs",
      totpSubLabel: "Authentification 2FA active",
      totpPlaceholder: "Code à 6 chiffres",
      totpDeviceSeed: "Générateur d'authentification synchronisé",
      totpHint: "Saisissez le code de vérification à 6 chiffres généré par votre application d'authentification",
      workspaceTitle: "Centre de Contrôle / V1",
      tabLeads: "Bóveda de Leads",
      tabSecurity: "IP d'autorisation",
      tabTelemetry: "Télémétrie en direct",
      lockBtn: "Verrouiller le tableau",
      leadsTitle: "Base de données de Leads",
      leadsDesc: "Requêtes de clients, messages et questions collectés via des formulaires cryptés.",
      archived: "Archivé",
      emptyLeadsTitle: "Aucune demande en attente !",
      emptyLeadsDesc: "Tous les échanges sécurisés avec les clients ont été accomplis. Remplissez le formulaire de contact pour ajouter des entrées !",
      reply: "Répondre",
      securityTitle: "Restrictions Pare-feu IP",
      securityDesc: "Configurez dynamiquement les coordonnées IP autorisées pour le client. Les accès hors liste déclencheront des masques 404.",
      allowedAddresses: "Adresses CIDR autorisées",
      activeClientBadge: "Votre client actif",
      ipFormLabel: "Autoriser une nouvelle IP de nœud",
      ipFormPlaceholder: "ex. 192.168.1.1",
      ipFormWarn: "Soyez extrêmement prudent et ne supprimez pas votre IP active de la liste autorisée pour éviter un blocage immédiat !",
      ipFormBtn: "Ajouter à la liste",
      emergencyReset: "Réinitialisation d'urgence",
      telemetryTitle: "Moniteur de performance système",
      telemetryDesc: "Données matérielles en temps réel compilées à partir de grappes de serveurs.",
      cpuLoad: "CHARGE CPU",
      cpuSub: "Micro-oscillations d'horloge",
      ramLoad: "ALLOCATION MÉMOIRE",
      ramSub: "Go sur 32,0 Go alloués",
      dbCore: "BÉQUILLE DE BASE DE DONNÉES",
      dbStatus: "ACTIF",
      dbInstance: "Instance PostgreSQL",
      dbSchema: "Schéma Siteforces actif",
      dbDelay: "Délai de réplication: 8ms",
    }
  },
  de: {
    common: {
      getInTouch: "Kontaktieren",
      visit: "Besuchen",
      reset: "Zurücksetzen",
      seconds: "s",
      back: "Zurück",
      required: "Erforderlich",
      verify: "Verifizieren",
      send: "Nachricht Senden",
    },
    navbar: {
      products: "Produkte",
      philosophy: "Philosophie",
      about: "Über Uns",
      contact: "Kontakt",
      cta: "Spur aufnehmen",
      navigation: "Navigation",
      emailUs: "careers@teralumax.com",
      ctaMobile: "Projekt Starten",
    },
    hero: {
      tag: "Handgefertigte Digitale Architektur",
      line1: "Produkte Bauen",
      line2: "Die Menschen Lieben.",
      sub: "Teralumax entwickelt wunderschön gestaltete Softwareprodukte und digitale Erlebnisse für moderne Unternehmen und alltägliche Nutzer.",
      ctaPrimary: "Produkte Erkunden",
      ctaSecondary: "Kontaktieren",
      scrollDown: "Herunterscrollen",
    },
    about: {
      philosophyTag: "Die Philosophie",
      header: "Handwerkskunst Vor Kompromiss",
      quotePart1: "Wir bauen Software mit ",
      quoteSimplicity: "Einfachheit",
      quotePart2: ", ",
      quotePrecision: "Präzision",
      quotePart3: " und absolutem ",
      quotePurpose: "Zweck",
      quotePart4: ".",
      specializations: "Spezialisierungen /",
      bento1Title: "Makellose Spezifikationen",
      bento1Desc: "Unsere Codebasen sind mit strengen Typgrenzen, modularen Systemen und Hardwareoptimierungen für maximale Reaktionsfähigkeit ausgestattet.",
      bento2Title: "Designgeführte Geschwindigkeit",
      bento2Desc: "Wir entwerfen parallel zur Entwicklung und entwickeln direkt polierte Vektorkurven, Mikro-Feedback und sensorische Mikro-Animationen.",
      bento3Title: "Menschlicher Nutzen",
      bento3Desc: "Jede digitale Schnittstelle sollte als natürlicher Begleiter der Geschäftslogik dienen und Arbeitsablaufbeschränkungen vereinfachen, anstatt sie zu erschweren.",
      words: [
        "SaaS-Plattformen",
        "Maßgeschneiderte Automatisierung",
        "Immersive Web-Apps",
        "Native mobile Apps",
        "Kognitive KI-Tools",
        "Robuster Cloud-Kern",
        "Mutige digitale Innovation",
      ],
    },
    roomy: {
      tag: "Produkt-Präsentation / 01",
      title: "Roomy",
      sub: "Aufwertung lokaler Raumarchitektur mit sauberer, cloud-nativer Raumverwaltungssoftware.",
      desc: "Roomy denkt Arbeitsplatzbuchungen neu. Benutzer beanspruchen Schreibtische in Echtzeit, synchronisieren Zeitpläne auf Teamboards und erhalten sofortigen visuellen Einblick in physische Raumauslastungen.",
      statOccupancy: "Belegungsstatus",
      statOccupancyDesc: "Aktuelle Auslastung in Echtzeit",
      statBookings: "Abgeschlossene Swipes",
      statBookingsDesc: "Sofortige API-Synchronisierungen",
      visitRoomy: "Roomy Besuchen",
    },
    siteforces: {
      tag: "Produkt-Präsentation / 02",
      title: "SiteForces",
      sub: "Entwurf modularer visueller Websites direkt verknüpft mit hochleistungsfähigen relationalen Datenbanken.",
      desc: "SiteForces stellt das Layout-Paradigma auf den Kopf. Erstellen Sie wunderschöne Blöcke, synchronisieren Sie Designtabellen und erleben Sie ultraschnelle Deployment-Pipelines zum Hosten vollständiger Datenbanken ohne Code-Komplexität.",
      tags: ["Low-Code Node Sync", "JSON Schema Output", "Visual Stylesheets", "Subsekunden Reload"],
      hudEngine: "SITEFORCES_KERN_ENGINE",
      hudState: "ENGINE_AKTIV",
      hudStatus: "STATUS: OPTIMAL_SYNC",
      hudNode: "Relationaler Website-Knoten",
      hudView: "Visuelle Siteforces Echtansicht",
      hudDesc: "Automatische visuelle Strukturbaum-Generierung direkt verknüpft mit relationalen Tabellen",
      hudLoad: "LADEZEIT",
      hudLoadSpeed: "0.02s EXTREM",
      visitSiteForces: "SiteForces Besuchen",
    },
    arrowescape: {
      tag: "Produkt-Präsentation / 03",
      title: "Arrow Escape: Infinite Maze",
      subtitle: "Original aus dem Google Play Store",
      sub: "Gleite, weiche aus und entkomme wunderschönen Neon-Labyrinthen von zunehmender Geschwindigkeit.",
      desc: "Arrow Escape verbindet Retro-Mechanik mit modernem visuellen Feedback. Manövriere um Hindernisse herum, berechne sofortige Spielzüge in engen Zeitfenstern und meistere geometrische Präzision.",
      statMoves: "Berechnete Spielzüge",
      statMovesUnit: "Schritte",
      statMovesUnitSingle: "Schritt",
      statStatus: "Labyrinth-Status",
      statusEscaped: "ENTKOMMEN!",
      statusActive: "Aktives Labyrinth Live",
      retry: "Zurücksetzen",
      emulatorInstruction: "Verwende die Pfeiltasten oder WASD, um zu navigieren. Erreiche das grüne Portal rechts unten.",
      playStoreCTA: "Auf Google Play entdecken",
    },
    careers: {
      tag: "Offene Wege",
      title: "Begleite Uns",
      desc: "Wir suchen ständig eigenverantwortliche Experten für Technik, Design und Produkt, die stolz auf pixelgenaue Präzision und performante Algorithmen sind.",
      applyCta: "Per E-Mail bewerben",
      applySub: "Sende deinen Lebenslauf an careers@teralumax.com",
      perks: [
        { title: "Mobiles Handwerk", text: "Asynchrone Autonomie mit fließenden, räumlich ungebundenen Arbeitsabläufen." },
        { title: "Progressive Grenzen", text: "Schreibe strikte, optimierte TS-Schnittstellen mit branchenprägenden Rendergeschwindigkeiten." },
        { title: "Kompromissloses Detail", text: "Keine Vorlagen von der Stange. Wir bauen maßgeschneiderte Lösungen mit präziser Hingabe." },
      ],
    },
    contact: {
      tag: "Kontakt",
      title: "Schreib uns",
      desc: "Hast du ein Projekt, ein Kollaborationsangebot oder möchtest du dich über unsere Softwareentwicklung Pipelines informieren? Schreib uns.",
      labelName: "Dein vollständiger Name",
      labelEmail: "E-Mail-Adresse",
      labelCompany: "Name des Unternehmens",
      labelMessage: "Projektumriss / Nachricht",
      placeholderName: "z.B. Liam Sterling",
      placeholderEmail: "z.B. liam@firma.de",
      placeholderCompany: "z.B. Sterling Holdings (Optional)",
      placeholderMessage: "Erzähl uns über deine Projektziele...",
      errName: "Bitte trage deinen Namen ein, um zu beginnen.",
      errEmail: "Bitte gib eine gültige E-Mail-Adresse an, damit wir dich erreichen können.",
      errMessage: "Bitte füge eine kurze Projektbeschreibung hinzu.",
      successTitle: "Übertragung erfolgreich",
      successText: "Dein geschütztes Datenpaket wurde weitergeleitet. Unser Entwicklerknoten wird innerhalb von 12 Standard-Sonnenzyklen antworten.",
      btnLoading: "Verschlüsseltes Payload wird übertragen...",
      btnSubmit: "Verbindung Herstellen",
    },
    footer: {
      philosophy: "Wir gestalten, programmieren und betreiben schlanke, elegante Produkte und Cloud-Schnittstellen für weltweit agierende Startups.",
      registeredOffice: "Sitz der Ges.: Delhi, IN",
      allRightsReserved: "Teralumax Private Limited. Alle Rechte vorbehalten.",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "Teralumax Sicherheits-Gateway",
      controlPanel: "Operatives Kontrollzentrum",
      nodeRef: "KNOTEN_REF: TL-990-SECURE",
      notFoundTitle: "404 Nicht Gefunden",
      notFoundText: "Die angeforderte URL wurde auf diesem Server nicht gefunden. Das ist alles, was wir wissen.",
      overrideTitle: "[NUR DEMO] Bypass-Autorisierung",
      overrideText: "Um den IP-Sicherheitsfilter zu testen, wurde deine aktive Adresse freigeschaltet. Klicke auf Autorisieren, um sie der Liste hinzuzufügen.",
      authorizeBtn: "AKTUELLE CLIENT-IP AUTORISIEREN",
      verificationRequired: "Sicherheitsprüfung erforderlich",
      verificationDesc: "Dieser Arbeitsplatz-Knoten ist durch strenge kryptografische Protokolle geschützt. Gib deine Handshake-Verbindungscodes ein.",
      passphraseLabel: "Alpha Seed-Verbindungsschlüssel",
      passphrasePlaceholder: "Administrativen Seed eingeben",
      passphraseBtn: "Handshake initiieren",
      totpLabel: "Multi-Faktor OTP Pin",
      totpSubLabel: "Aktives synchronisiertes 2FA",
      totpPlaceholder: "6-stelligen Code eingeben",
      totpDeviceSeed: "Echtzeit-Generator-Hardware",
      totpHint: "Geben Sie den aktiven 6-stelligen Verifizierungscode aus Ihrer Authenticator-App ein",
      workspaceTitle: "Leitstand / V1",
      tabLeads: "Leads Tresor",
      tabSecurity: "IP-Autorisierung",
      tabTelemetry: "Live-Telemetrie",
      lockBtn: "Dashboard sperren",
      leadsTitle: "Leads & Kontakte Datenbank",
      leadsDesc: "Kundenanfragen und Nachrichten, gesichert erfasst über verschlüsselte Online-Formulare.",
      archived: "Archiviert",
      emptyLeadsTitle: "Keine ausstehenden Leads",
      emptyLeadsDesc: "Alle synchronisierten Kundengespräche wurden erfolgreich erfasst. Fülle das Kontaktformular aus, um neue Testdaten zu generieren!",
      reply: "Antworten",
      securityTitle: "IP-Firewall Einschränkungen",
      securityDesc: "Konfiguriere zulässige IP-Addresskoordinaten im Live-System. Unbefugte IPs erhalten ein Standardmasken-404.",
      allowedAddresses: "Zulässige CIDR-Addressen",
      activeClientBadge: "Deine aktive IP",
      ipFormLabel: "Neuen Netzknoten autorisieren",
      ipFormPlaceholder: "z.B. 192.168.1.1",
      ipFormWarn: "Achte unbedingt darauf, deine aktive Adresse nicht aus der Liste zu entfernen, da du dich sonst sofort selbst aussperrst!",
      ipFormBtn: "Zur Whitelist hinzufügen",
      emergencyReset: "Standardwerte laden (Sicherheits-Notfall)",
      telemetryTitle: "System Performance Monitor",
      telemetryDesc: "Hardwareauslastung der genutzten Cloud-Ressourcen in Echtzeit.",
      cpuLoad: "CPU-AUSLASTUNG",
      cpuSub: "Taktzyklen im Subsekunden-Bereich",
      ramLoad: "RAM-SPEICHER",
      ramSub: "GB von 32.0 GB zugewiesen",
      dbCore: "DATENBANK CORE",
      dbStatus: "AKTIV",
      dbInstance: "PostgreSQL Instanz",
      dbSchema: "Siteforces Schema aktiv",
      dbDelay: "Replikations-Delay: 8ms",
    }
  },
  hi: {
    common: {
      getInTouch: "संपर्क करें",
      visit: "देखें",
      reset: "रीसेट करें",
      seconds: "सेकंड",
      back: "पीछे",
      required: "अनिवार्य",
      verify: "सत्यापित करें",
      send: "संदेश भेजें",
    },
    navbar: {
      products: "उत्पाद",
      philosophy: "दर्शन",
      about: "हमारे बारे में",
      contact: "संपर्क",
      cta: "संपर्क करें",
      navigation: "नेविगेशन",
      emailUs: "careers@teralumax.com",
      ctaMobile: "परियोजना शुरू करें",
    },
    hero: {
      tag: "शिल्प डिजिटल वास्तुकला",
      line1: "उत्पाद बनाना",
      line2: "जिन्हें लोग पसंद करें।",
      sub: "टेरालूमैक्स आधुनिक व्यवसायों और रोजमर्रा के उपयोगकर्ताओं के लिए खूबसूरती से तैयार किए गए सॉफ़्टवेयर उत्पाद और डिजिटल अनुभव बनाता है।",
      ctaPrimary: "उत्पादों का अन्वेषण करें",
      ctaSecondary: "संपर्क करें",
      scrollDown: "नीचे स्क्रॉल करें",
    },
    about: {
      philosophyTag: "सिद्धांत",
      header: "समझौते पर शिल्प कौशल",
      quotePart1: "हम सॉफ़्टवेयर का निर्माण ",
      quoteSimplicity: "सादगी",
      quotePart2: ", ",
      quotePrecision: "सटीकता",
      quotePart3: " और पूर्ण ",
      quotePurpose: "उद्देश्य",
      quotePart4: " के साथ करते हैं।",
      specializations: "विशेषज्ञताएं /",
      bento1Title: "त्रुटिहीन विनिर्देश",
      bento1Desc: "हमारी कोडबेस कड़े प्रकार के नियमों, मॉड्यूलर सिस्टम और हार्डवेयर अनुकूलन के साथ बनाई गई हैं ताकि प्रतिक्रिया समय कम से कम हो।",
      bento2Title: "डिजाइन-आधारित गति",
      bento2Desc: "हम विकास के समानांतर डिजाइन करते हैं, सीधे पॉलिश वेक्टर वक्र, सूक्ष्म प्रतिक्रिया और संवेदी सूक्ष्म एनिमेशन बनाते हैं।",
      bento3Title: "मानवीय उपयोगिता",
      bento3Desc: "प्रत्येक डिजिटल इंटरफ़ेस को व्यावसायिक तर्क के एक प्राकृतिक साथी के रूप में कार्य करना चाहिए, जो वर्कफ़्लो की सीमाओं को जटिल बनाने के बजाय उन्हें सरल बनाए।",
      words: [
        "सास प्लेटफॉर्म",
        "अनुकूलित स्वचालन",
        "इमर्सिव वेब ऐप्स",
        "मूल मोबाइल ऐप्स",
        "संज्ञानात्मक एआई उपकरण",
        "मजबूत क्लाउड कोर",
        "साहसिक डिजिटल नवाचार",
      ],
    },
    roomy: {
      tag: "उत्पाद का प्रदर्शन / 01",
      title: "Roomy",
      sub: "स्वच्छ, क्लाउड-नेटिव रूम प्रबंधन सॉफ़्टवेयर के साथ स्थानीय स्थानिक वास्तुकला को बढ़ाना।",
      desc: "Roomy कार्यक्षेत्र के निर्धारण की पुनर्कल्पना करता है। उपयोगकर्ता वास्तविक समय में डेस्क का दावा करते हैं, टीम बोर्डों पर शेड्यूल को सुव्यवस्थित करते हैं, और तत्काल भौतिक सुविधा सीमाओं पर दृश्य अंतर्दृष्टि प्राप्त करते हैं।",
      statOccupancy: "अधिभोग स्थिति",
      statOccupancyDesc: "लाइव उपयोग दर",
      statBookings: "संपूर्ण स्वाइप",
      statBookingsDesc: "तत्काल एपीआई सिंक्रोनाइज़ेशन",
      visitRoomy: "Roomy पर जाएं",
    },
    siteforces: {
      tag: "उत्पाद का प्रदर्शन / 02",
      title: "SiteForces",
      sub: "उच्च प्रदर्शन वाले रिलेशनल डेटाबेस से सीधे जुड़े मॉड्यूलर विज़ुअल वेबसाइटों का डिज़ाइन।",
      desc: "SiteForces लेआउट प्रतिमान को पूरी तरह से बदल देता है। शानदार ब्लॉक बनाएं, स्टाइलिंग टेबल को सिंक्रोनाइज़ करें, और कोड जटिलता के बिना पूर्ण डेटाबेस को होस्ट करने के लिए इंजीनियर की गई बेहद तेज़ परिनियोजन पाइपलाइनों का अनुभव करें।",
      tags: ["कम कोड नोड सिंक", "JSON स्कीमा आउटपुट", "दृश्य स्टाइलशीट", "अति-त्वरित रीलोड"],
      hudEngine: "साइटफोर्स_कोर_इंजन",
      hudState: "इंजन_सक्रिय",
      hudStatus: "स्थिति: सर्वोत्कृष्ट_सिंक",
      hudNode: "रिलेशनल वेबसाइट नोड",
      hudView: "विजुअल डायनेमिक साइटफोर्स व्यू",
      hudDesc: "सीधे रिलेशनल टेबल से जुड़े विजुअल ट्री का स्वचालित जनरेशन",
      hudLoad: "पेज लोड समय",
      hudLoadSpeed: "0.02s अत्यंत तीव्र",
      visitSiteForces: "SiteForces पर जाएं",
    },
    arrowescape: {
      tag: "उत्पाद का प्रदर्शन / 03",
      title: "Arrow Escape: Infinite Maze",
      subtitle: "गूगल प्ले स्टोर ओरिजिनल",
      sub: "बढ़ती हुई गति के सुंदर नियॉन चक्रव्यूह के माध्यम से घूमें, फिसलें और बचकर निकलें।",
      desc: "Arrow Escape आधुनिक विजुअल फीडबैक के साथ रेट्रो मैकेनिक्स को जोड़ता है। बाधाओं से बचें, त्वरित समय-सीमा के तहत त्वरित चालों की गणना करें और ज्यामितीय सटीकता में महारत हासिल करें।",
      statMoves: "परिकलित चालें",
      statMovesUnit: "कदम",
      statMovesUnitSingle: "कदम",
      statStatus: "भूलभुलैया की स्थिति",
      statusEscaped: "बच निकले!",
      statusActive: "सक्रिय चक्रव्यूह लाइव",
      retry: "रीसेट",
      emulatorInstruction: "नेविगेट करने के लिए तीर कुंजियों या WASD का उपयोग करें। नीचे-दाएं कोने में स्थित हरे पोर्टल (समाप्ति) तक पहुंचें।",
      playStoreCTA: "गूगल प्ले पर खोजें",
    },
    careers: {
      tag: "खुले रास्ते",
      title: "हमारे सफर से जुड़ें",
      desc: "हम लगातार स्व-निर्देशित इंजीनियरिंग, डिज़ाइन और उत्पाद सामान्यज्ञों की तलाश कर रहे हैं जो पिक्सेल सटीकता और उच्च प्रदर्शन वाले एल्गोरिदम में गर्व महसूस करते हैं।",
      applyCta: "ईमेल के माध्यम से आवेदन करें",
      applySub: "अपना सीवी careers@teralumax.com पर भेजें",
      perks: [
        { title: "रिमोट क्राफ्ट्समैनशिप", text: "तरल स्थानिक कार्यप्रवाह के साथ अतुल्यकालिक स्वायत्तता।" },
        { title: "अग्रणी तकनीकी सीमाएं", text: "उद्योग-अग्रणी रेंडरिंग गतियों के साथ सख्त, अनुकूलित टीएस इंटरफेस लिखें।" },
        { title: "विस्तार पर पूर्ण ध्यान", text: "कोई तैयार टेम्पलेट नहीं। हम त्रुटिहीन देखभाल के साथ कस्टम समाधान बनाते हैं।" },
      ],
    },
    contact: {
      tag: "संपर्क करें",
      title: "सम्पर्क में रहें",
      desc: "क्या आपके पास कोई परियोजना है, सहयोग का अवसर है, या केवल हमारे सॉफ़्टवेयर इंजीनियरिंग पाइपलाइनों के बारे में पूछताछ करना चाहते हैं? नीचे लिखें।",
      labelName: "आपका पूरा नाम",
      labelEmail: "ईमेल पता",
      labelCompany: "कंपनी का नाम",
      labelMessage: "परियोजना की रूपरेखा / संदेश",
      placeholderName: "उदा. लियाम स्टर्लिंग",
      placeholderEmail: "उदा. liam@company.com",
      placeholderCompany: "उदा. स्टर्लिंग होल्डिंग्स (वैकल्पिक)",
      placeholderMessage: "अपने उद्देश्यों के बारे में बताएं...",
      errName: "शुरू करने के लिए कृपया अपना नाम भरें।",
      errEmail: "कृपया एक वैध ईमेल पता प्रदान करें ताकि हम आपसे संपर्क कर सकें।",
      errMessage: "कृपया अपनी परियोजना का विवरण देते हुए एक छोटा संदेश दर्ज करें।",
      successTitle: "सफल संचार",
      successText: "आपका सुरक्षित पैकेट भेज दिया गया है। हमारा कोर इंजीनियरिंग नोड 12 चक्रों के भीतर प्रतिक्रिया शुरू करेगा।",
      btnLoading: "एन्क्रिप्टेड पेलोड प्रसारित किया जा रहा है...",
      btnSubmit: "हैंडशेक प्रेषित करें",
    },
    footer: {
      philosophy: "हम दुनिया भर के डिजिटल नेटिव स्टार्टअप्स के लिए हल्के सुरुचिपूर्ण उत्पाद और क्लाउड इंटरफेस डिजाइन, कोड और परिनियोजित करते हैं।",
      registeredOffice: "पंजीकृत कार्यालय: दिल्ली, भारत",
      allRightsReserved: "टेरालूमैक्स प्राइवेट लिमिटेड। सर्वाधिकार सुरक्षित।",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "टेरालूमैक्स सुरक्षित गेटवे",
      controlPanel: "परिचालन नियंत्रण कक्ष",
      nodeRef: "नोड_संदर्भ: TL-990-SECURE",
      notFoundTitle: "404 अप्रत्याशित त्रुटि",
      notFoundText: "अनुरोधित URL इस सर्वर पर नहीं मिला। हमें बस इतना ही पता है।",
      overrideTitle: "[केवल डेमो के लिए] अधिकृत बाईपास गेट",
      overrideText: "आईपी प्रतिबंध यांत्रिकी का परीक्षण करने के लिए, आपके सक्रिय आईपी के लिए सुरक्षा को निलंबित कर दिया गया था। सूची में जोड़ने के लिए अधिकृत पर क्लिक करें।",
      authorizeBtn: "वर्तमान क्लाइंट आईपी को अधिकृत करें",
      verificationRequired: "सुरक्षित सत्यापन आवश्यक",
      verificationDesc: "यह वर्कस्टेशन नोड सख्त क्रिप्टोग्राफिक हैंडशेक के तहत छिपा है। रिकॉर्ड सिंक करने के लिए सत्यापन कोड प्रदान करें।",
      passphraseLabel: "अल्फा सीड पासफ़्रेज़",
      passphrasePlaceholder: "प्रशासकीय बीड दर्ज करें",
      passphraseBtn: "सत्यापन शुरू करें",
      totpLabel: "मल्टी-फैक्टर ओटीपी पिन",
      totpSubLabel: "सक्रिय सिंक्रोनाइज़्ड 2FA",
      totpPlaceholder: "6 अंकों का कोड दर्ज करें",
      totpDeviceSeed: "सत्यापन प्रदाता हार्डवेयर",
      totpHint: "अपने प्रमाणक एप्लिकेशन से सक्रिय 6-अंकीय सत्यापन कोड दर्ज करें",
      workspaceTitle: "नियंत्रण केंद्र / V1",
      tabLeads: "लीड्स तिजोरी",
      tabSecurity: "आईपी प्राधिकरण",
      tabTelemetry: "लाइव टेलीमेट्री",
      lockBtn: "डैशबोर्ड लॉक करें",
      leadsTitle: "लीड्स और संपर्क डेटाबेस",
      leadsDesc: "एन्क्रिप्टेड फॉर्म के माध्यम से एकत्र किए गए क्लाइंट संदेश और पूछताछ।",
      archived: "संग्रहीत",
      emptyLeadsTitle: "कोई लंबित लीड नहीं है !",
      emptyLeadsDesc: "सभी सुरक्षित ग्राहक कनेक्शन सफलतापूर्वक पूरे हो गए हैं। नया डेटा जोड़ने के लिए वेबसाइट पर संपर्क फ़ॉर्म भरें!",
      reply: "उत्तर दें",
      securityTitle: "आईपी फ़ायरवॉल प्रतिबंध",
      securityDesc: "अनुमत क्लाइंट आईपी निर्देशांकों को गतिशील रूप से कॉन्फ़िगर करें। अनधिकृत आईपी को 404 त्रुटि दिखाई देगी।",
      allowedAddresses: "अनुमत सीआईडीआर पते",
      activeClientBadge: "आपका सक्रिय आईपी",
      ipFormLabel: "नया नोड आईपी अधिकृत करें",
      ipFormPlaceholder: "उदा. 192.168.1.1",
      ipFormWarn: "सावधान रहें कि अनुमत सूची से अपना सक्रिय आईपी न हटाएं अन्यथा आप तुरंत लॉक हो जाएंगे!",
      ipFormBtn: "सूची में जोड़ें",
      emergencyReset: "डिफ़ॉल्ट सेटिंग्स लोड करें (आपातकालीन रीसेट)",
      telemetryTitle: "सिस्टम प्रदर्शन मॉनिटर",
      telemetryDesc: "क्लाउड सर्वर से संकलित लाइव हार्डवेयर संसाधन उपयोग डेटा।",
      cpuLoad: "सीपीयू लोड",
      cpuSub: "उप-सेकंड घड़ी का उतार-चढ़ाव",
      ramLoad: "मेमोरी आवंटन",
      ramSub: "32.0 जीबी में से आवंटित रैम",
      dbCore: "डेटाबेस कोर",
      dbStatus: "सक्रिय",
      dbInstance: "PostgreSQL इंस्टेंस",
      dbSchema: "साइटफोर्स स्कीमा सक्रिय",
      dbDelay: "प्रतिकृति देरी: 8ms",
    }
  },
  zh: {
    common: {
      getInTouch: "取得联系",
      visit: "访问",
      reset: "重置",
      seconds: "秒",
      back: "返回",
      required: "必填",
      verify: "验证",
      send: "发送消息",
    },
    navbar: {
      products: "软件产品",
      philosophy: "设计哲学",
      about: "关于我们",
      contact: "联系我们",
      cta: "取得联系",
      navigation: "导航",
      emailUs: "careers@teralumax.com",
      ctaMobile: "启动项目",
    },
    hero: {
      tag: "精工数字架构",
      line1: "构建产品",
      line2: "深受人们喜爱。",
      sub: "Teralumax 创造精美的软件产品和数字体验，专为现代企业和日常用户设计。",
      ctaPrimary: "探索产品",
      ctaSecondary: "取得联系",
      scrollDown: "向下滚动",
    },
    about: {
      philosophyTag: "经营哲学",
      header: "精益求精，绝不妥协",
      quotePart1: "我们以",
      quoteSimplicity: "极简",
      quotePart2: "、",
      quotePrecision: "精准",
      quotePart3: "和绝对的",
      quotePurpose: "目标",
      quotePart4: "构建软件。",
      specializations: "专业领域 /",
      bento1Title: "无可挑剔的规格",
      bento1Desc: "我们的代码库采用严格的类型界定、模块化系统和硬件优化，以实现极速响应。",
      bento2Title: "设计驱动的速度",
      bento2Desc: "我们同步进行设计与开发，直接构建精美的矢量曲线、微反馈和多维感官微动画。",
      bento3Title: "以人为本的实用性",
      bento3Desc: "每个数字界面都应作为业务逻辑的自然搭档，简化工作流难题，而非使其复杂化。",
      words: [
        "SaaS 软件平台",
        "定制自动化",
        "沉浸式 Web 应用",
        "原生移动应用",
        "认知型 AI 工具",
        "强大的云核心",
        "大胆的数字创新",
      ],
    },
    roomy: {
      tag: "产品展示 / 01",
      title: "Roomy",
      sub: "利用纯净、云原生的空间管理软件，提升本地空间架构的使用效率。",
      desc: "Roomy 重新定义了工作空间调度。用户可以实时预订桌面、跨团队看板同步日程表，并立即获得物理设施利用情况的直观洞察。",
      statOccupancy: "空间占用状态",
      statOccupancyDesc: "实时利用率",
      statBookings: "已完成扫码次数",
      statBookingsDesc: "即时 API 同步",
      visitRoomy: "访问 Roomy",
    },
    siteforces: {
      tag: "产品展示 / 02",
      title: "SiteForces",
      sub: "打造模块化可视化网站，并使之直接同高性能关系型数据库无缝连接。",
      desc: "SiteForces 颠覆了传统网站设计范式。创建精美的业务板块，同步样式表格，体验超高速部署通道，无需复杂代码即可托管完整的数据库系统。",
      tags: ["低代码节点同步", "JSON Schema 输出", "可视化样式表", "亚秒级极速重载"],
      hudEngine: "SITEFORCES_核心引擎",
      hudState: "引擎活动中",
      hudStatus: "状态: 最佳同步",
      hudNode: "关系型网站节点",
      hudView: "可视化动态 Siteforces 视图",
      hudDesc: "自动生成直连关系型数据表的可视化树状图",
      hudLoad: "页面加载时间",
      hudLoadSpeed: "0.02秒 极速加载",
      visitSiteForces: "访问 SiteForces",
    },
    arrowescape: {
      tag: "产品展示 / 03",
      title: "Arrow Escape: Infinite Maze",
      subtitle: "Google Play Store 原创游戏",
      sub: "在充满复合速度的精美霓虹迷宫之中穿梭、滑行并成功逃脱。",
      desc: "Arrow Escape 将经典复古机制与现代视觉反馈完美结合。绕过障碍，在极短的时限内精准计算每一步移动，掌握几何级别的极速操控。欢迎在右侧体验内置的模拟器！",
      statMoves: "已计算的步数",
      statMovesUnit: "步",
      statMovesUnitSingle: "步",
      statStatus: "迷宫状态",
      statusEscaped: "成功逃脱！",
      statusActive: "活体迷宫引擎激活中",
      retry: "重置迷宫",
      emulatorInstruction: "使用方向键或 WASD 进行移动。到达右下角的绿色传送门（终点）即可通关。",
      playStoreCTA: "在 Google Play 下载体验",
    },
    careers: {
      tag: "开放通道",
      title: "共同见证未来",
      desc: "我们长期寻找具备自驱力的工程、设计和产品通用型人才，希望能与对像素级精度和高性能算法有极致追求的伙伴一路同行。",
      applyCta: "通过邮件申请",
      applySub: "发送简历至 careers@teralumax.com",
      perks: [
        { title: "远程匠心", text: "兼具柔性空间流程的异步、高自主性工作体系。" },
        { title: "高维技术边界", text: "编写严格、高度优化的 TS 类型系统，实现行业标准的极速渲染。" },
        { title: "绝不忽视细节", text: "不套用模板。我们以追求极致艺术品的态度，纯手工打磨每一套业务解决方案。" },
      ],
    },
    contact: {
      tag: "联系我们",
      title: "与我们取得联系",
      desc: "有具体的项目想法、合作契机，或者只想了解我们的软件工程系统？请在下方给我们留言。",
      labelName: "您的全名",
      labelEmail: "电子邮箱地址",
      labelCompany: "公司名称",
      labelMessage: "项目计划 / 留言内容",
      placeholderName: "例如：Liam Sterling",
      placeholderEmail: "例如：liam@company.com",
      placeholderCompany: "例如：Sterling 集团（选填）",
      placeholderMessage: "请向我们简要描述您的项目目标...",
      errName: "请先填写您的全名以开始联系。",
      errEmail: "请输入有效的邮箱地址，以便我们与您取得联系。",
      errMessage: "请输入一小段简要消息来描述您的项目需求。",
      successTitle: "握手通信成功",
      successText: "您的安全数据包已被发送。我们的核心工程节点将在 12 个标准太阳周期内为您启动定制回复方案。",
      btnLoading: "正在传输高强度加密数据负载...",
      btnSubmit: "发起加密通信握手",
    },
    footer: {
      philosophy: "我们为全球数字化时代的初创企业，设计、编写并部署轻量优雅的软件产品和坚实可靠的云端物理架构。",
      registeredOffice: "注册办公地址: 印度德里",
      allRightsReserved: "Teralumax Private Limited. 保留所有权利。",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "Teralumax 安全隔离网关",
      controlPanel: "业务运行中枢控制台",
      nodeRef: "隔离节点: TL-990-SECURE",
      notFoundTitle: "404 页面未找到",
      notFoundText: "在当前的主机节点上未检索到您请求的资源。服务器正以屏蔽模式保护本站。",
      overrideTitle: "[仅限演示] 自助授权通道",
      overrideText: "为了便于测试防火墙机制，系统感知到了您的未授权访问。点击下方授权按钮，自动将您的本地 IP 追加至安全白名单。",
      authorizeBtn: "自动授权我当前的本地 IP",
      verificationRequired: "核心验证握手",
      verificationDesc: "当前工作站处于极高加密状态。请提供双因子验证种子代码以同步业务日志。",
      passphraseLabel: "甲级密钥种子密码",
      passphrasePlaceholder: "输入管理员种子密钥",
      passphraseBtn: "建立初级密钥握手",
      totpLabel: "多因子验证 Pin 码",
      totpSubLabel: "活跃且实时同步的 2FA 的验证",
      totpPlaceholder: "输入 6 位验证码",
      totpDeviceSeed: "认证权标同步生成器",
      totpHint: "请输入身份验证器应用程序中的 6 位动态验证码",
      workspaceTitle: "隔离中枢控制台 / V1",
      tabLeads: "业务 Leads 仓储",
      tabSecurity: "IP 防火墙授权",
      tabTelemetry: "实时物理网卡监控",
      lockBtn: "彻底锁闭工作站",
      leadsTitle: "业务 Leads 数据库表",
      leadsDesc: "经由前台加密通道采集的客户方案需求和合作契机表。",
      archived: "已归档",
      emptyLeadsTitle: "完美！无积压待审 Lead 记录",
      emptyLeadsDesc: "当前的未归档前台联系记录为空。您可以通过在前台填写联系表单，从而在这里测试实时的数据同步！",
      reply: "立刻电邮回复",
      securityTitle: "IP 防火墙硬件防火规则",
      securityDesc: "动态配置允许接入当前管理后台的主机 IP 坐标。任何来自白名单以外网络的访问都会静默强制跳转至 404 页面。",
      allowedAddresses: "CIDR 准入白名单",
      activeClientBadge: "当前设备活跃 IP",
      ipFormLabel: "手动准入新虚拟节点 IP",
      ipFormPlaceholder: "例如：192.168.1.1",
      ipFormWarn: "警告！请务必不要手动移除您本身正在使用的通信 IP，否则系统将立即彻底把您拒之门外！",
      ipFormBtn: "写入白名单规则",
      emergencyReset: "初始化默认防火墙规则 (紧急救援)",
      telemetryTitle: "网卡及主机性能监视器",
      telemetryDesc: "来自云端生产集群的物理网卡、CPU 的 sub-second 级别实时负荷监测。",
      cpuLoad: "CPU 计算负荷",
      cpuSub: "秒级高频率时钟抖动率",
      ramLoad: "内存物理占用率",
      ramSub: "GB 物理内存已分配 (共 32.0 GB)",
      dbCore: "数据库底层运行状态",
      dbStatus: "状态良好 ACTIVE",
      dbInstance: "云端 PostgreSQL 容器实例",
      dbSchema: "Siteforces 动态数据结构活跃中",
      dbDelay: "主从复制延迟时间: 8毫秒",
    }
  },
  ja: {
    common: {
      getInTouch: "メッセージを送る",
      visit: "アクセスする",
      reset: "リセット",
      seconds: "秒",
      back: "戻る",
      required: "必須",
      verify: "確認",
      send: "送信する",
    },
    navbar: {
      products: "プロダクト",
      philosophy: "フィロソフィー",
      about: "事業内容",
      contact: "お問い合わせ",
      cta: "メッセージを送る",
      navigation: "ナビゲーション",
      emailUs: "careers@teralumax.com",
      ctaMobile: "プロジェクト開始",
    },
    hero: {
      tag: "職人のデジタルアーキテクチャ",
      line1: "プロダクト構築",
      line2: "人々に愛されるもの。",
      sub: "Teralumax（テラルマックス）は、現代のビジネスと日常のユーザー向けに美しく精巧に作られたソフトウェア製品とデジタル体験を創造します。",
      ctaPrimary: "プロダクトを探索",
      ctaSecondary: "メッセージを送る",
      scrollDown: "下にスクロール",
    },
    about: {
      philosophyTag: "フィロソフィー",
      header: "妥協なき職人技",
      quotePart1: "私たちは、",
      quoteSimplicity: "シンプルさ",
      quotePart2: "、",
      quotePrecision: "正確さ",
      quotePart3: "、そして絶対的な",
      quotePurpose: "目的",
      quotePart4: "をもってソフトウェアを構築します。",
      specializations: "専門分野 /",
      bento1Title: "完璧な仕様",
      bento1Desc: "私たちのコードベースは、厳密な型定義、モジュラーシステム、およびハードウェア最適化によって設計されており、応答性を最大限に高めています。",
      bento2Title: "デザイン主導のベロシティ",
      bento2Desc: "開発と並行してデザインを行い、研ぎ澄まされたベクター曲線、マイクロフィードバック、感覚的なマイクロアニメーションを直接エンジニアリングします。",
      bento3Title: "人間中心のユーティリティ",
      bento3Desc: "すべてのデジタルインターフェースは、ビジネスロジックの自然なパートナーとして機能し、ワークフローを複雑にするのではなくシンプルにする必要があります。",
      words: [
        "SaaSプラットフォーム",
        "カスタム自動化",
        "没入型Webアプリ",
        "ネイティブモバイルアプリ",
        "認知型AIツール",
        "堅牢なクラウドコア",
        "大胆なデジタル革新",
      ],
    },
    roomy: {
      tag: "ショーケース / 01",
      title: "Roomy",
      sub: "シンプルでクラウドネイティブな会議室・スペース管理システムにより、空間設計を最適化します。",
      desc: "Roomyは、ワークスペースの予約スケジュールを再定義します。ユーザーはリアルタイムでデスクを確保し、チームボードにスケジュールを同期させ、物理施設の稼働状況を瞬時に視覚化できます。",
      statOccupancy: "スペース利用状態",
      statOccupancyDesc: "リアルタイムの稼働率",
      statBookings: "総チェックイン回数",
      statBookingsDesc: "シームレスなAPI自動同期",
      visitRoomy: "Roomyを見る",
    },
    siteforces: {
      tag: "ショーケース / 02",
      title: "SiteForces",
      sub: "高性能なリレーショナルデータベースとリアルタイム連携する、直感的なモジュール式ウェブサイトデザインツール。",
      desc: "SiteForcesはレイアウトの概念を一新します。洗練されたモジュールブロックを作成し、スタイルテーブルを同期させ、コードを書くことなくデータベース全体をホスト可能な超高速デプロイシステムを提供します。",
      tags: ["ノーコードでのノード同期", "JSONスキーマ出力定義", "ビジュアルスタイルシート", "超高速リロード"],
      hudEngine: "SITEFORCES_CORE_エンジン",
      hudState: "稼働中",
      hudStatus: "ステータス: 同期完了",
      hudNode: "リレーショナルウェブノード",
      hudView: "インタラクティブSiteforcesビュー",
      hudDesc: "リレーショナルデータテーブルから直接変換されたビジュアルツリーの自動生成",
      hudLoad: "読込速度",
      hudLoadSpeed: "0.02秒 極限アクセス",
      visitSiteForces: "SiteForcesを見る",
    },
    arrowescape: {
      tag: "ショーケース / 03",
      title: "Arrow Escape: Infinite Maze",
      subtitle: "Google Playストア オリジナル作品",
      sub: "スピードが加速する美しいネオンの迷宮を、滑り抜け、かいくぐり、脱出せよ。",
      desc: "Arrow Escapeは、レトロな物理メカニクスと近代的な視覚フィードバックを融合させました。障害物をかわし、時一刻と迫る制限のなかで最適な瞬時の動作を導き出します。右側のミニエミュレータでお試しください！",
      statMoves: "計算された総移動数",
      statMovesUnit: "手",
      statMovesUnitSingle: "手",
      statStatus: "迷宮ステータス",
      statusEscaped: "脱出成功！",
      statusActive: "迷宮システム同期中",
      retry: "リセット",
      emulatorInstruction: "矢印キーまたはWASDキーで移動します。右下のグリーンのポータル（ゴール）に到達してください。",
      playStoreCTA: "Google Playで詳しく見る",
    },
    careers: {
      tag: "採用情報",
      title: "新しい価値を共に",
      desc: "私たちは、ピクセル単位の描写精度と非常に高度で効率的なアルゴリズム設計に情熱を注ぐ、主体的なエンジニア、デザイナー、製品のジェネラリストを常に求めています。",
      applyCta: "メールで応募する",
      applySub: "履歴書・職務経歴書を careers@teralumax.com へお送りください",
      perks: [
        { title: "リモート型の職人技", text: "非同期での自律性が確保された柔軟なワークスペース体系。" },
        { title: "先端的な技術スタック", text: "型定義を徹底した高パフォーマンスなTypeScriptコードの追求、圧倒的な描画速度。" },
        { title: "ディテールへの強いこだわり", text: "テンプレートに過度に依存せず、細部まで丁寧にビスポークソリューションを作り込みます。" },
      ],
    },
    contact: {
      tag: "お問い合わせ",
      title: "お問い合わせ窓口",
      desc: "特定のプロジェクト、技術的なコラボレーション、またはシステムソリューションに関するご相談など、何でも以下からお寄せください。",
      labelName: "お名前（フルネーム）",
      labelEmail: "メールアドレス",
      labelCompany: "貴社名・組織名",
      labelMessage: "プロジェクト概要 / 内容",
      placeholderName: "例: リアム・スターリング",
      placeholderEmail: "例: liam@company.com",
      placeholderCompany: "例: スターリング・ホールディングス（任意）",
      placeholderMessage: "システム要件、ビジネス課題などをご記入ください...",
      errName: "最初にお名前を入力してください。",
      errEmail: "返信連絡が可能な正しいメールアドレスを入力してください。",
      errMessage: "要件を説明する短いメッセージを入力してください（10文字以上）。",
      successTitle: "送信完了しました",
      successText: "安全にセッションログが暗号化され送信されました。当社のエンジニアリング部門より、12標準太陽周期以内にご案内いたします。",
      btnLoading: "接続を確立し、データを送信中...",
      btnSubmit: "暗号化接続で送信",
    },
    footer: {
      philosophy: "私たちは、世界中の先進的なデジタルスタートアップ企業向けに、軽量でエレガントなWebプロダクトとクラウドインターフェースを企画・設計・構築・展開します。",
      registeredOffice: "商業名簿登録: デリー, インド",
      allRightsReserved: "Teralumax Private Limited. All rights reserved.",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "Teralumax セキュリティゲートウェイ",
      controlPanel: "稼働管理コントロールパネル",
      nodeRef: "防衛ノード: TL-990-SECURE",
      notFoundTitle: "404 Not Found",
      notFoundText: "お探しのファイルまたはリソースはサーバー上で検出されませんでした。セキュリティ機能が動作しています。",
      overrideTitle: "[シミュレータ用] 管理者IP自己承認ゲート",
      overrideText: "IP自動マスキング機能を簡易検証するため、管理者によって一時的にこの自己解決枠が表示されています。許可をクリックすると白リストへと追加されます。",
      authorizeBtn: "現在接続中のクライアントIPをホワイトリストに登録",
      verificationRequired: "セキュリティ検証を要求中",
      verificationDesc: "この内部管理ワークステーションは厳重な暗号強度のもと稼働します。同期用の認証キーを提供してください。",
      passphraseLabel: "アルファシード・機密パスフレーズ",
      passphrasePlaceholder: "管理者シードキーを入力",
      passphraseBtn: "相互鍵交換を開始する",
      totpLabel: "多要素認証ワンタイムPin",
      totpSubLabel: "アクティブかつリアルタイム同期される2FAの検証",
      totpPlaceholder: "6桁の番号を入力",
      totpDeviceSeed: "同期されたバーチャル権標デバイス",
      totpHint: "認証システムアプリから有効な6桁の確認コードを入力してください",
      workspaceTitle: "隔離コントロールセンター / V1",
      tabLeads: "お問い合わせ(Leads)",
      tabSecurity: "ファイアウォール設定",
      tabTelemetry: "物理ステータス監視",
      lockBtn: "中枢コンソールを完全ロック",
      leadsTitle: "収集された Leads & お問い合わせログ",
      leadsDesc: "前台暗号通信モジュールから伝送された、企業のプロジェクト引き合いのローデータ。",
      archived: "アーカイブ済",
      emptyLeadsTitle: "クリーン！未対応のLeadsはありません",
      emptyLeadsDesc: "すべてのお問い合わせ案件と顧客のハンドシェイクに問題なく対処されました。前台フォームから新しいダミーデータを伝送できます！",
      reply: "电郵にて返信する",
      securityTitle: "IPファイアウォール・ルーティング制限",
      securityDesc: "ウェブサーバーへのログインを許可する管理端末のIPを管理します。リスト外からのリクエストは自動で静的に404として偽装されます。",
      allowedAddresses: "CIDR ホワイトリスト範囲",
      activeClientBadge: "現在の自端末アクティブIP",
      ipFormLabel: "手動での管理用IP追加",
      ipFormPlaceholder: "例: 192.168.1.1",
      ipFormWarn: "警告！あなたが現在使用している接続IPアドレスを誤ってホワイトリストから外すと、二度と入ることができなくなるため十分配慮して操作してください！",
      ipFormBtn: "ホワイトリストに規則を追加",
      emergencyReset: "システム定義を初期値にリセット (緊急復旧)",
      telemetryTitle: "仮想ハードウェア性能監視",
      telemetryDesc: "クラウドサーバーにマウントされている物理CPUやネットワークカードの負荷特性の超高速監視結果。",
      cpuLoad: "CPU 計算負荷特質",
      cpuSub: "秒単位クロックの小波揺らぎ比率",
      ramLoad: "物理メモリ消費特性",
      ramSub: "GB 物理メモリが割り当て済み（総計 32.0 GB）",
      dbCore: "データベース中枢性能",
      dbStatus: "正常稼働 ACTIVE",
      dbInstance: "クラウド PostgreSQL コンテナインスタンス",
      dbSchema: "Siteforces リアルタイムスキーマ接続中",
      dbDelay: "マスタースレーブ複製遅延: 8ミリ秒",
    }
  },
  ar: {
    common: {
      getInTouch: "تواصل معنا",
      visit: "زيارة",
      reset: "إعادة ضبط",
      seconds: "ثانية",
      back: "عودة",
      required: "مطلوب",
      verify: "تحقق",
      send: "إرسال الرسالة",
    },
    navbar: {
      products: "المنتجات",
      philosophy: "الفلسفة",
      about: "حولنا",
      contact: "اتصل بنا",
      cta: "تواصل معنا",
      navigation: "التنقل",
      emailUs: "careers@teralumax.com",
      ctaMobile: "ابدأ مشروعاً",
    },
    hero: {
      tag: "بنية رقمية متقنة",
      line1: "صناعة المنتجات",
      line2: "التي يحبها الناس.",
      sub: "تقوم تيرالومكس بإنشاء منتجات برمجية وتجارب رقمية مصممة بشكل جميل ومصممة للشركات الحديثة والمستخدمين اليوميين.",
      ctaPrimary: "استكشف المنتجات",
      ctaSecondary: "تواصل معنا",
      scrollDown: "تمرير لأسفل",
    },
    about: {
      philosophyTag: "الفلسفة والنهج",
      header: "الحرفية تفوق المساومة",
      quotePart1: "نحن نبني البرمجيات بـ",
      quoteSimplicity: "بساطة",
      quotePart2: "، و",
      quotePrecision: "دقة",
      quotePart3: "، وهدف ",
      quotePurpose: "مطلق",
      quotePart4: ".",
      specializations: "التخصصات /",
      bento1Title: "مواصفات لا تشوبها شائبة",
      bento1Desc: "تم تصميم قواعد الأكواد لدينا بحدود نوع صارمة، وأنظمة معيارية، وتكاملات لتسريع الاستجابة لأقصى حد.",
      bento2Title: "السرعة المدفوعة بالتصميم",
      bento2Desc: "نحن نصمم بالتوازي مع التطوير، ونصمم منحنيات متجهة مصقولة، وتعليقات دقيقة، ورسوم متحركة حسية مباشرة.",
      bento3Title: "الفائدة البشرية",
      bento3Desc: "يجب أن تعمل كل واجهة رقمية كرفيق طبيعي لمنطق الأعمال، مما يبسط سير العمل بدلاً من تعقيده وتأخيره.",
      words: [
        "منصات الخدمة البرمجية",
        "الأتمتة المخصصة والمتقنة",
        "تطبيقات ويب انغماسية",
        "تطبيقات الهواتف الأصلية",
        "أدوات الذكاء الإدراكي",
        "نواة سحابية فائقة الأداء",
        "الابتكار الرقمي الجسور",
      ],
    },
    roomy: {
      tag: "استعراض المنتجات / 01",
      title: "Roomy",
      sub: "الارتقاء بالبنية المكانية المحلية باستخدام برنامج إدارة الغرف النظيف والمتوافق مع السحابة.",
      desc: "يعيد Roomy تخيل جدولة مساحات العمل. يطالب المستخدمون بالمكاتب في الوقت الفعلي، ويزامنون جداولهم عبر لوحات جماعية، ويحصلون على رؤى بصرية فورية حول حدود المرافق والمنشأة.",
      statOccupancy: "حالة إشغال الغرف",
      statOccupancyDesc: "معدل الاستخدام الفعلي المباشر",
      statBookings: "عمليات التمرير المكتملة",
      statBookingsDesc: "تزامنات API اللحظية",
      visitRoomy: "قم بزيارة Roomy",
    },
    siteforces: {
      tag: "استعراض المنتجات / 02",
      title: "SiteForces",
      sub: "تصميم مواقع مرئية معيارية ترتبط على الفور بقواعد بيانات علائقية عالية الأداء والسرعة.",
      desc: "يقلب SiteForces نموذج تصميم الصفحات رأساً على عقب. أنشئ كتلًا رائعة، ومزامنة قوالب الأنماط، وجرب خطوط نشر فائقة السرعة مهيأة لاستضافة قواعد بيانات كاملة دون أي تعقيد برمجي.",
      tags: ["تزامن منخفض الأكواد", "مخرجات مخططات JSON", "أنماط مرئية مميزة", "إعادة تحميل في أجزاء من الثانية"],
      hudEngine: "محرك_SITEFORCES_المركزي",
      hudState: "المحرك_شغال",
      hudStatus: "الحالة: متزامن_بشكل_أمثل",
      hudNode: "عقدة موقع الويب العلائقي",
      hudView: "رؤية Siteforces المرئية الحركية",
      hudDesc: "توليد تلقائي للهيكل الشجري المرتبط مباشرة بجداول قاعدة البيانات العلائقية",
      hudLoad: "سرعة التحميل",
      hudLoadSpeed: "0.02ث فائقة للغاية",
      visitSiteForces: "قم بزيارة SiteForces",
    },
    arrowescape: {
      tag: "استعراض المنتجات / 03",
      title: "Arrow Escape: Infinite Maze",
      subtitle: "اللعبة الأصلية في متجر Google Play",
      sub: "انسج، تزلج، واهرب عبر متاهات نيون خلابة تتضاعف فيها السرعة باستمرار.",
      desc: "تجمع Arrow Escape بين آليات الألعاب الكلاسيكية والتغذية الراجعة المرئية الحديثة. انزلق حول العقبات، واحسب الحركات الفورية تحت وطأة ضيق الوقت، واحترف الدقة الهندسية. جرب المحاكي المصغر باليمين!",
      statMoves: "الخطوات المحسوبة",
      statMovesUnit: "خطوات",
      statMovesUnitSingle: "خطوة",
      statStatus: "حالة المتاهة",
      statusEscaped: "لقد هربت بنجاح!",
      statusActive: "مستشعرات المتاهة نشطة وفي تزامن دائم",
      retry: "إعادة ضبط المتاهة",
      emulatorInstruction: "استخدم أسهم لوحة المفاتيح أو أزرار WASD للتنقل والوصول إلى البوابة الخضراء (النهاية) في أسفل اليمين لربح التحدي.",
      playStoreCTA: "استكشف على متجر Google Play",
    },
    careers: {
      tag: "مسارات وقنوات مفتوحة",
      title: "انضم إلى مسيرتنا الجسورة",
      desc: "نحن نبحث باستمرار عن مهندسين ومصممين متميزين ومطورين ذوي توجهات ذاتية يفخرون بإنتاج جودة برمجية متقنة تلامس البكسل الواحد بالغة الأداء.",
      applyCta: "تقدم عبر البريد الإلكتروني",
      applySub: "أرسل سيرتك الذاتية وتفاصيل أعمالك إلى careers@teralumax.com",
      perks: [
        { title: "الحرفية الرقمية عن بعد", text: "استقلالية عمل غير متزامنة مع مسارات تدفق مرئية سلسة تماماً." },
        { title: "حدود تقنية متطورة للغاية", text: "اكتب واجهات TypeScript صارمة ومُحسّنة بأدق التفاصيل والسرعات القياسية الصارمة." },
        { title: "تركيز كلي على أدق التفاصيل", text: "رؤيتنا لا تعتمد الحزم البرمجية المعتادة بل نبتكر حلولاً متكاملة بدقة وجودة لا تضاهى." },
      ],
    },
    contact: {
      tag: "تواصل معنا",
      title: "دعنا نتواصل معًا",
      desc: "هل لديك فكرة مشروع، فرصة للتعاون المشترك، أو ترغب ببساطة في الاستفسار عن برمجيات تيرالومكس المتطورة؟ اكتب لنا أدناه.",
      labelName: "اسمك الكامل",
      labelEmail: "عنوان بريدك الإلكتروني",
      labelCompany: "اسم الشركة / المؤسسة",
      labelMessage: "مخطط المشروع / محتوى الرسالة",
      placeholderName: "مثال: عبد الرحمن بن فهد",
      placeholderEmail: "مثال: abdulrahman@company.com",
      placeholderCompany: "مثال: تيرالومكس القابضة (اختياري)",
      placeholderMessage: "أخبرنا بإيجاز عن التحديات أو أهداف مشروعك البرمجي...",
      errName: "بالرجاء تعبئة اسمك الكامل لبدء المحادثة.",
      errEmail: "يرجى تقديم عنوان بريد إلكتروني صالح للاستجابة السريعة وتنسيق الاتصال.",
      errMessage: "يرجى إدخال رسالة قصيرة توضح أهداف وتطلعات مشروعك بدقة.",
      successTitle: "نجاح الاتصال والتوثيق",
      successText: "تم بنجاح تشفير بياناتك وإرسالها بأمن تام. ستبدأ وحدة الهندسة والردود المخصصة لدينا التواصل معك في غضون 12 دورة شمسية قياسية.",
      btnLoading: "جارٍ تشفير البيانات وبث الحمولة للحوسبة الآمنة...",
      btnSubmit: "إرسال حزمة الاتصال الأمنة",
    },
    footer: {
      philosophy: "نحن نصمم، نبرمج، وننشر منتجات برمجية خفيفة الوزن وحلولاً سحابية متقدمة في غاية الأناقة للشركات السحابية الصاعدة حول العالم.",
      registeredOffice: "المكتب المسجل: دلهي، الهند",
      allRightsReserved: "تيرالومكس المحدودة. كافة الحقوق محفوظة.",
      regText: "REG_DELHI_IN",
    },
    admin: {
      gatewayTitle: "بوابة تيرالومكس الأمنية المغلقة",
      controlPanel: "لوحة التحكم الحية للأنظمة والتشغيل",
      nodeRef: "العقدة المحمية: TL-990-SECURE",
      notFoundTitle: "404 غير موجود",
      notFoundText: "الصفحة أو الملف البرمجي الذي طلبته لم يتم العثور عليه في المسارات السحابية. محاكاة الدفاع نشطة لحماية الخادم.",
      overrideTitle: "[خاص بالاختبار فقط] ميزة الترخيص التلقائي للمصادقة",
      overrideText: "لتجربة واختبار قدرات تصفية وجدار حماية الآي بي، استشعرنا وصولك غير المرخص من الـ IP الخاص بك. اضغط على تخويل لتسجيله تلقائياً في قائمة الوصول.",
      authorizeBtn: "تخويل وتسجيل عنوان الآي بي الخاص بجهازي الحالي",
      verificationRequired: "يلزم إجراء مصادقة أمنية للولوج",
      verificationDesc: "هذه المحطة الإدارية الخاصة محمية بأطواق تشفير بالغة الشدة. يرجى إدخال كود المصادقة متعدد العوامل لفتح التدفقات واستعراض السجلات.",
      passphraseLabel: "عبارة مرور مفتاح بذور ألفا التشفيرية",
      passphrasePlaceholder: "أدخل مفتاح الإدخال الإداري",
      passphraseBtn: "إبرام المصافحة والتوثيق الأولي",
      totpLabel: "كود التحقق متعدد العوامل Pin",
      totpSubLabel: "رمز التحقق الثنائي النشط والمتزامن لحظياً 2FA",
      totpPlaceholder: "أدخل الرمز المكون من 6 أرقام",
      totpDeviceSeed: "مولد الرموز الافتراضي المتزامن",
      totpHint: "أدخل رمز التحقق المكون من 6 أرقام النشط من تطبيق المصادقة الخاص بك",
      workspaceTitle: "مركز التحكم المعزول / غطاء V1",
      tabLeads: "صندوق الاتصال والشركات (Leads)",
      tabSecurity: "جدار حماية الآي بي",
      tabTelemetry: "حالة النظام وموارد المعالجة",
      lockBtn: "إغلاق وقفل محطة العمل كلياً",
      leadsTitle: "قاعدة بيانات leads والشركات المهتمة",
      leadsDesc: "استمارات الاتصال، والمشاريع، وفحوى الرسائل الواردة المشفرة من واجهة الموقع الرئيسية في حوسبة أمنة.",
      archived: "مؤرشف",
      emptyLeadsTitle: "نظيف تمامًا! لا توجد طلبات معلقة بانتظار المراجعة",
      emptyLeadsDesc: "تم حسم والرد على كافة طلبات الاتصال والمصافحة بشكل قانوني وأنيق. يمكنك إرسال استمارة وهمية من الموقع الرئيسي لتظهر هنا على الفور للتجربة!",
      reply: "الرد مباشرة بالإيميل",
      securityTitle: "قيود وحظر جدار حماية الآي بي",
      securityDesc: "تحكم ديناميكياً بآي بي الأجهزة المخولة بفتح لوحة الإدارة لضمان أمن الأنظمة. أي محاولة من خارج القائمة يتم خداعها بصفحة 404 صامتة.",
      allowedAddresses: "نطاقات CIDR المرخصة للولوج المباشر",
      activeClientBadge: "الآي بي النشط لجهازك الحالي",
      ipFormLabel: "إضافة آي بي جهاز جديد يدويًا",
      ipFormPlaceholder: "مثال: 192.168.1.1",
      ipFormWarn: "تحذير شديد! يرجى الانتباه ألا تقوم بحذف الآي بي الخاص بجهازك الحالي الذي تتصفح منه اللوحة، وإلا سيقوم النظام بطردك وقفل اللوحة عنك على الفور!",
      ipFormBtn: "إضافة وحفظ في قائمة الترخيص",
      emergencyReset: "استعادة افتراضيات جدار الحماية (حالات الطوارئ)",
      telemetryTitle: "مستشعرات ومعمل مراقبة موارد العتاد",
      telemetryDesc: "رصد مباشر لبطاقة المعالجة، كرت الشبكة، والذاكرة في أجزاء من الثانية من البنية التحتية السحابية المضيفة.",
      cpuLoad: "معدل استهلاك المعالج CPU Load",
      cpuSub: "تردد ذبذبات ساعة المعالجة بالملي ثانية",
      ramLoad: "استهلاك الذاكرة العشوائية RAM Allocation",
      ramSub: "جيجابايت مستهلكة بحرص (من أصل 32.0 جيجابايت)",
      dbCore: "نواة وحالة قاعدة البيانات",
      dbStatus: "نشط OPTIMAL",
      dbInstance: "قاعدة بيانات PostgreSQL سحابية",
      dbSchema: "مخططات وجداول Siteforces نشطة وتعمل",
      dbDelay: "فارق وتأخير تزامن النسخ الاحتياطي: 8 ملي ثانية",
    }
  },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // 1. Prioritize URL query parameters (essential for Search Engine Crawlers indexation)
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get("lang");
    if (urlLang && ["en", "es", "fr", "de", "hi", "zh", "ja", "ar"].includes(urlLang)) {
      return urlLang as Language;
    }

    // 2. Fallback to Local Storage session parameters
    const saved = localStorage.getItem("teralumax_language");
    if (saved && ["en", "es", "fr", "de", "hi", "zh", "ja", "ar"].includes(saved)) {
      return saved as Language;
    }
    
    // 3. Fallback to browser primary language
    const browserLang = navigator.language?.split("-")[0];
    if (browserLang && ["en", "es", "fr", "de", "hi", "zh", "ja", "ar"].includes(browserLang)) {
      return browserLang as Language;
    }
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("teralumax_language", lang);
    localStorage.setItem("teralumax_language_manually_set", "true");
    sessionStorage.setItem("teralumax_language_manually_set", "true");

    // Sync URL search parameters dynamically for dynamic deep-linking
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", lang);
      window.history.replaceState({}, "", url.toString());
    } catch (e) {
      console.warn("Could not sync URL state", e);
    }
  };

  const currentConfig = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  useEffect(() => {
    // Dynamic layout handling for RTL languages (Arabic etc.)
    document.documentElement.dir = currentConfig.dir;
    document.documentElement.lang = language;
  }, [language, currentConfig]);

  // Geographical language autodetection from client IP if no manual override exists
  useEffect(() => {
    const isManuallySet = 
      localStorage.getItem("teralumax_language_manually_set") === "true" ||
      sessionStorage.getItem("teralumax_language_manually_set") === "true";
    
    if (isManuallySet) return;

    let active = true;
    let timeoutId: any;
    
    const detectGeoLanguage = async () => {
      const controller = new AbortController();
      const abortTimeout = setTimeout(() => {
        controller.abort();
      }, 800);

      try {
        // Fetch from a robust IP-geolocation directory API
        const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
        clearTimeout(abortTimeout);
        if (!res.ok) throw new Error("Geolocation call failing");
        const data = await res.json();
        
        if (!active) return;
        const country = data.country_code?.toUpperCase();
        
        if (country) {
          let detected: Language = "en";
          // Map country codes to standard Teralumax language translations
          if (["ES", "MX", "AR", "CO", "PE", "VE", "CL", "EC", "GT", "CU", "BO", "DO", "HN", "PY", "SV", "NI", "CR", "PA", "UY", "GQ"].includes(country)) {
            detected = "es";
          } else if (["FR", "CA", "BE", "CH", "MC", "LU", "SN", "CD", "CG", "CI", "NE", "BF", "ML", "GN", "BI", "BJ", "TG", "GA"].includes(country)) {
            detected = "fr";
          } else if (["DE", "AT", "LI"].includes(country)) {
            detected = "de";
          } else if (country === "IN") {
            detected = "en";
          } else if (["CN", "TW", "HK", "SG"].includes(country)) {
            detected = "zh";
          } else if (country === "JP") {
            detected = "ja";
          } else if (["AE", "SA", "EG", "DZ", "MA", "SD", "IQ", "YE", "JO", "SY", "LB", "LY", "OM", "KW", "QA", "BH", "TN"].includes(country)) {
            detected = "ar";
          }
          
          // Apply detected geographical context to Language state
          setLanguageState(detected);
          localStorage.setItem("teralumax_language", detected);
        }
      } catch (err) {
        clearTimeout(abortTimeout);
        console.warn("Could not retrieve client-side geographical metadata from IP address", err);
      }
    };

    timeoutId = setTimeout(() => {
      detectGeoLanguage();
    }, 1200);

    return () => {
      active = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const t = (key: string, section: string = "common", fallback?: string): any => {
    let targetSection = section;
    let targetKey = key;

    // Handle "products" section mapping
    if (section === "products") {
      if (key === "roomySub") { targetSection = "roomy"; targetKey = "sub"; }
      else if (key === "roomyDesc") { targetSection = "roomy"; targetKey = "desc"; }
      else if (key === "siteforcesSub") { targetSection = "siteforces"; targetKey = "sub"; }
      else if (key === "siteforcesDesc") { targetSection = "siteforces"; targetKey = "desc"; }
      else if (key === "arrowSub") { targetSection = "arrowescape"; targetKey = "sub"; }
      else if (key === "arrowDesc") { targetSection = "arrowescape"; targetKey = "desc"; }
    }

    // 1. Check moreTranslations in active language
    const moreSecDict = moreTranslations[language]?.[targetSection];
    if (moreSecDict && moreSecDict[targetKey] !== undefined) {
      return moreSecDict[targetKey];
    }

    // 2. Check main translations in active language
    const secDict = translations[language]?.[targetSection];
    if (secDict && secDict[targetKey] !== undefined) {
      return secDict[targetKey];
    }

    // 3. Fallback to English moreTranslations
    const moreEnSecDict = moreTranslations["en"]?.[targetSection];
    if (moreEnSecDict && moreEnSecDict[targetKey] !== undefined) {
      return moreEnSecDict[targetKey];
    }

    // 4. Fallback to English main translations
    const enVal = translations["en"]?.[targetSection]?.[targetKey];
    if (enVal !== undefined) {
      return enVal;
    }

    // 5. Fallback parameter if provided, else targetKey
    return fallback !== undefined ? fallback : targetKey;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, currentConfig }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
